import {
  IColorMap,
  IDualColorMap,
  IDualGradientColorMap,
} from "types/IForecastMap";
import { t } from "i18next";
import {
  getRainCategoryColormap,
  getSnowCategoryColormap,
  getRainGradientColors,
  getSnowGradientColors,
  buildCSSGradient,
} from "./RadarDbzMapper";

/**
 * Rainbow AI Radar Colormap (color=0 - Rainbow palette)
 *
 * This colormap matches the Rainbow AI default palette (color=0) which uses
 * a rainbow-style color scheme for precipitation intensity.
 *
 * Rain vs Snow Differentiation:
 * - Rainbow AI uses the highest bit (128) of the red channel for snow detection
 * - If (R & 128) == 128: Snow
 * - If (R & 128) == 0: Rain
 * - Actual reflectivity: (R & 127) - 32 dBZ
 *
 * Color Scale (approximate mm/h values based on dBZ conversion):
 * - Very light: 0.1-0.5 mm/h (light blue/cyan)
 * - Light: 0.5-2 mm/h (blue/green)
 * - Moderate: 2-10 mm/h (yellow/orange)
 * - Heavy: 10-50 mm/h (red/magenta)
 * - Extreme: 50+ mm/h (pink/white)
 *
 * Note: These are approximate values. Rainbow AI provides tiles with encoded
 * precipitation data, and the exact mapping depends on their internal algorithms.
 */
export const getRadarColormap = (): IColorMap => {
  return {
    colormap: [
      // Rainbow AI color=0 palette (approximate)
      { value: 0.0, color: "rgba(146, 136, 113, 0.1)" }, // No precipitation (transparent)
      { value: 0.1, color: "#d0e8ff" }, // Very light - pale blue
      { value: 0.5, color: "#6eb5ff" }, // Light - light blue
      { value: 1.0, color: "#1e90ff" }, // Light-moderate - blue
      { value: 2.0, color: "#00c800" }, // Moderate - green
      { value: 5.0, color: "#ffff00", textColor: "#000" }, // Moderate-heavy - yellow
      { value: 10, color: "#ffaa00" }, // Heavy - orange
      { value: 25, color: "#ff5500" }, // Very heavy - red-orange
      { value: 50, color: "#ff0000" }, // Extreme - red
      { value: 100, color: "#ff00ff" }, // Exceptional - magenta
    ],
    humanizedScale: {
      skip: 1,
      scales: [
        {
          label: t("radarLegend.light"),
          length: 3, // 0.1, 0.5, 1.0
        },
        {
          label: t("radarLegend.moderate"),
          length: 3, // 2.0, 5.0, 10
        },
        {
          label: t("radarLegend.heavy"),
          length: 3, // 25, 50, 100
        },
      ],
    },
    displayValues: [0.0, 0.1, 0.5, 1.0, 2.0, 5.0, 10, 25, 50, 100],
    precision: 1,
    unit: "mm/h",
    unitSymbol: "mm/h",
  };
};

/**
 * Returns dual colormap structure for displaying both rain and snow
 * intensity categories side by side in a stacked vertical layout.
 *
 * This function provides the category-based colormaps used by the
 * DualColorMapLegend component to render separate rain and snow legends
 * with Light/Moderate/Heavy intensity categories based on dBZ ranges.
 *
 * @returns IDualColorMap with rain and snow category colormaps
 */
export const getDualRadarColormap = (): IDualColorMap => {
  return {
    rain: {
      colormap: getRainCategoryColormap(),
      type: "rain",
    },
    snow: {
      colormap: getSnowCategoryColormap(),
      type: "snow",
    },
  };
};

/**
 * Returns intensity scale labels for rain colormap with calculated positions
 * Positions are calculated based on dBZ range 0-95
 *
 * @returns Array of scale labels with positions (Light at 10.5%, Moderate at 31.6%, Heavy at 71.1%)
 */
const getRainScaleLabels = (): Array<{ label: string; position: number }> => {
  const DBZ_MAX = 50;

  // Calculate center position for each dBZ range
  const lightCenter = (0 + 20) / 2; // 10 dBZ
  const moderateCenter = (20 + 40) / 2; // 30 dBZ
  const heavyCenter = (40 + 50) / 2; // 45 dBZ

  return [
    {
      label: t("radarLegend.light"),
      position: (lightCenter / DBZ_MAX) * 100, // 20%
    },
    {
      label: t("radarLegend.moderate"),
      position: (moderateCenter / DBZ_MAX) * 100, // 60%
    },
    {
      label: t("radarLegend.heavy"),
      position: (heavyCenter / DBZ_MAX) * 100, // 90%
    },
  ];
};

/**
 * Returns mm/h value labels for rain colormap overlay
 * Positions are calculated using Marshall-Palmer Z-R relation: Z = 200 × R^1.6
 *
 * Conversion formula:
 * - Z = 200 × R^1.6 (where R is mm/h)
 * - dBZ = 10 × log10(Z)
 * - position = (dBZ / 50) × 100 (for 0-50 dBZ range)
 *
 * Calculated positions (rounded values for better distribution):
 * - 0.2 mm/h → 12.6 dBZ → 25%
 * - 1 mm/h → 23.0 dBZ → 46%
 * - 3 mm/h → 30.8 dBZ → 62%
 * - 10 mm/h → 38.0 dBZ → 76%
 * - 40 mm/h → 44.5 dBZ → 89%
 *
 * @returns Array of mm/h labels with positions to display on gradient bar
 */
const getRainMmhLabels = (): Array<{ label: string; position: number }> => {
  return [
    { label: "mm/h", position: 8 },
    { label: "0.2", position: 25 },
    { label: "1", position: 46 },
    { label: "3", position: 62 },
    { label: "10", position: 76 },
    { label: "50+", position: 93 },
  ];
};

/**
 * Returns dual gradient colormap structure for continuous rain/snow display
 *
 * Uses the complete 128-color Rainbow AI table (dBZ -32 to 95) to generate
 * smooth CSS linear-gradient strings for both rain and snow precipitation types.
 *
 * This provides Windy-style continuous color interpolation with full color fidelity.
 *
 * @returns IDualGradientColorMap with CSS gradients and precipitation type labels
 */
export const getDualRadarGradientColormap = (): IDualGradientColorMap => {
  const rainColors = getRainGradientColors();
  const snowColors = getSnowGradientColors();

  return {
    rain: {
      gradient: buildCSSGradient(
        rainColors.map((c) => ({ dbz: c.dbz, color: c.color }))
      ),
      colors: rainColors,
      type: "rain",
      label: t("radarLegend.rain"),
      scaleLabels: getRainScaleLabels(),
      mmhLabels: getRainMmhLabels(),
    },
    snow: {
      gradient: buildCSSGradient(
        snowColors.map((c) => ({ dbz: c.dbz, color: c.color }))
      ),
      colors: snowColors,
      type: "snow",
      label: t("radarLegend.snow"),
    },
  };
};
